export const PERFORMANCE_METRIC_OPTIONS = [
  {
    id: 'open_rate',
    label: 'Open Rate',
    tableSubtitleMetric: 'Opened',
  },
  {
    id: 'unsubscribed',
    label: 'Unsubscribed',
    tableSubtitleMetric: 'Unsubscribed',
  },
  {
    id: 'click_rate',
    label: 'Click Rate',
    tableSubtitleMetric: 'Clicked',
  },
  {
    id: 'email_sent',
    label: 'Email sent',
    tableSubtitleMetric: 'Sent',
  },
  {
    id: 'spam_complaints',
    label: 'Spam Complaints',
    tableSubtitleMetric: 'Spam Complaints',
  },
];

export const CAMPAIGN_FILTER_OPTIONS = [
  { id: 'all', label: 'All Campaigns' },
  { id: 'email', label: 'Email Campaign' },
  { id: 'workflow', label: 'Workflow Campaign' },
  { id: 'bulk', label: 'Bulk Action Campaign' },
];

export const ENGAGEMENT_LEGEND = [
  { id: 'email', label: 'Email Campaign', color: '#1e40af' },
  { id: 'workflow', label: 'Workflow Campaign', color: '#7c3aed' },
  { id: 'bulk', label: 'Bulk Action Campaign', color: '#38bdf8' },
];

export const DEFAULT_DATE_RANGE = {
  start: '2026-04-07',
  end: '2026-07-05',
};

export const MOCK_PERFORMANCE_SUMMARY = {
  delivered: 3704,
  bounced: 60,
  unsubscribed: 6,
  spamComplaints: 1,
  opened: 457,
  clicked: 19,
  ordered: 0,
  openRate: 12.34,
};

export const MOCK_ENGAGEMENT_FUNNEL = [
  { id: 'delivered', label: 'Delivered', value: 3704, cumulativePercent: 100 },
  { id: 'opened', label: 'Opened', value: 457, cumulativePercent: 12 },
  { id: 'clicked', label: 'Clicked', value: 19, cumulativePercent: 0 },
  { id: 'ordered', label: 'Ordered', value: 0, cumulativePercent: 0 },
];

export const MOCK_TOP_EMAIL_ROWS = [
  {
    id: 'flash-sale',
    title: 'Flash Sale Campaign',
    executionDate: '2026-07-05T01:35:00Z',
    delivered: 5949,
    opened: 404,
    clicked: 19,
    unsubscribed: 2,
    spamComplaints: 0,
    type: 'Email Campaign',
  },
  {
    id: 'confirmation',
    title: 'Send Confirmation Email',
    executionDate: null,
    delivered: 271,
    opened: 49,
    clicked: 3,
    unsubscribed: 0,
    spamComplaints: 0,
    type: 'Workflow Campaign',
  },
  {
    id: 'generic-email',
    title: 'Email',
    executionDate: null,
    delivered: 383,
    opened: 52,
    clicked: 1,
    unsubscribed: 1,
    spamComplaints: 0,
    type: 'Workflow Campaign',
  },
  {
    id: 'welcome-series',
    title: 'Welcome Series - Day 1',
    executionDate: '2026-06-12T14:20:00Z',
    delivered: 1204,
    opened: 156,
    clicked: 11,
    unsubscribed: 0,
    spamComplaints: 0,
    type: 'Workflow Campaign',
  },
  {
    id: 'bulk-promo',
    title: 'Summer Promo Blast',
    executionDate: '2026-05-18T09:00:00Z',
    delivered: 812,
    opened: 98,
    clicked: 7,
    unsubscribed: 1,
    spamComplaints: 0,
    type: 'Bulk Action Campaign',
  },
];

export function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(Number(value || 0));
}

export function calculateRate(numerator, denominator) {
  const top = Number(numerator || 0);
  const bottom = Number(denominator || 0);

  if (!bottom) return 0;

  return (top / bottom) * 100;
}

export function formatRate(numerator, denominator) {
  return `${calculateRate(numerator, denominator).toFixed(2)}%`;
}

export function getMetricOption(metricId) {
  return PERFORMANCE_METRIC_OPTIONS.find((option) => option.id === metricId)
    || PERFORMANCE_METRIC_OPTIONS[0];
}

export function getMetricValue(row, metricId) {
  switch (metricId) {
    case 'open_rate':
      return calculateRate(row.opened, row.delivered);
    case 'click_rate':
      return calculateRate(row.clicked, row.delivered);
    case 'email_sent':
      return Number(row.delivered || 0);
    case 'unsubscribed':
      return Number(row.unsubscribed || 0);
    case 'spam_complaints':
      return Number(row.spamComplaints || 0);
    default:
      return calculateRate(row.opened, row.delivered);
  }
}

export function sortRowsByMetric(rows, metricId) {
  return [...rows].sort(
    (a, b) => getMetricValue(b, metricId) - getMetricValue(a, metricId),
  );
}

export function getOpenRateDisplay(row, showStatisticsInNumbers) {
  if (showStatisticsInNumbers) {
    return formatNumber(row.opened);
  }

  return formatRate(row.opened, row.delivered);
}

export function getClickRateDisplay(row, showStatisticsInNumbers) {
  if (showStatisticsInNumbers) {
    return formatNumber(row.clicked);
  }

  return formatRate(row.clicked, row.delivered);
}

export function getChartTitle(metricId) {
  switch (metricId) {
    case 'click_rate':
      return 'Click Rate (for All Campaigns)';
    case 'unsubscribed':
      return 'Unsubscribed (for All Campaigns)';
    case 'email_sent':
      return 'Email Sent (for All Campaigns)';
    case 'spam_complaints':
      return 'Spam Complaints (for All Campaigns)';
    case 'open_rate':
    default:
      return 'Open Rate (for All Campaigns)';
  }
}

export function getTableSubtitle(metricId) {
  const option = getMetricOption(metricId);
  return `List of top performing emails based on ${option.tableSubtitleMetric}`;
}

export function formatDateRangeLabel(start, end) {
  if (!start || !end) return '';
  return `${start} → ${end}`;
}

export function formatExecutionDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).replace(',', '');
}

export function parseIsoDate(value) {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

export function toIsoDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getQuickRangeDays(days) {
  const end = new Date(2026, 6, 5);
  const start = new Date(end);
  start.setDate(end.getDate() - (days - 1));
  return {
    start: toIsoDate(start),
    end: toIsoDate(end),
  };
}

export function getMetricSummaryDisplay(metricId, summary, showNumbers) {
  switch (metricId) {
    case 'click_rate':
      return {
        label: 'Click Rate',
        value: showNumbers
          ? formatNumber(summary.clicked)
          : formatRate(summary.clicked, summary.delivered),
        footer: [
          { label: 'Total Clicked', value: formatNumber(summary.clicked) },
          { label: 'Total Delivery', value: formatNumber(summary.delivered) },
        ],
      };
    case 'unsubscribed':
      return {
        label: 'Unsubscribed',
        value: showNumbers ? formatNumber(summary.unsubscribed) : formatRate(summary.unsubscribed, summary.delivered),
        footer: [
          { label: 'Total Unsubscribed', value: formatNumber(summary.unsubscribed) },
          { label: 'Total Delivery', value: formatNumber(summary.delivered) },
        ],
      };
    case 'email_sent':
      return {
        label: 'Email sent',
        value: formatNumber(summary.delivered),
        footer: [
          { label: 'Total Sent', value: formatNumber(summary.delivered) },
          { label: 'Total Delivery', value: formatNumber(summary.delivered) },
        ],
      };
    case 'spam_complaints':
      return {
        label: 'Spam Complaints',
        value: showNumbers ? formatNumber(summary.spamComplaints) : formatRate(summary.spamComplaints, summary.delivered),
        footer: [
          { label: 'Total Complaints', value: formatNumber(summary.spamComplaints) },
          { label: 'Total Delivery', value: formatNumber(summary.delivered) },
        ],
      };
    case 'open_rate':
    default:
      return {
        label: 'Open Rate',
        value: showNumbers ? formatNumber(summary.opened) : `${Number(summary.openRate || calculateRate(summary.opened, summary.delivered)).toFixed(2)}%`,
        footer: [
          { label: 'Total Opened', value: formatNumber(summary.opened) },
          { label: 'Total Delivery', value: formatNumber(summary.delivered) },
        ],
      };
  }
}

function seededNoise(index, seed = 1) {
  const x = Math.sin((index + 1) * seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

export function buildMockMetricSeries(metricId, startIso, endIso) {
  const start = parseIsoDate(startIso) || parseIsoDate(DEFAULT_DATE_RANGE.start);
  const end = parseIsoDate(endIso) || parseIsoDate(DEFAULT_DATE_RANGE.end);
  const points = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    const month = cursor.getMonth();
    const day = cursor.getDate();
    const base = metricId === 'email_sent'
      ? 0.35 + seededNoise(day, month + 1) * 0.15
      : 0.004 + seededNoise(day, month + 2) * 0.006;

    let spike = 0;
    if (month === 4 && day >= 10 && day <= 25) {
      spike = 0.012 + seededNoise(day, 3) * 0.006;
    }
    if (month === 5 && day >= 12 && day <= 18) {
      spike = 0.016 + seededNoise(day, 4) * 0.004;
    }

    const value = metricId === 'email_sent'
      ? Math.round(80 + (base + spike) * 420)
      : Number(((base + spike) * 100).toFixed(2));

    points.push({
      date: toIsoDate(cursor),
      label: cursor.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }),
      value,
      emailCampaign: metricId === 'email_sent' ? Math.round(value * 0.45) : Number((value * 0.42).toFixed(2)),
      workflowCampaign: metricId === 'email_sent' ? Math.round(value * 0.4) : Number((value * 0.38).toFixed(2)),
      bulkActionCampaign: metricId === 'email_sent' ? Math.round(value * 0.15) : Number((value * 0.2).toFixed(2)),
    });

    cursor.setDate(cursor.getDate() + 5);
  }

  return points;
}

export function getEngagementBarWidth(value, maxValue) {
  if (!maxValue) return 0;
  return Math.max(0, Math.min(100, (value / maxValue) * 100));
}
